import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import { bulkBankStatus, fetchBankStatusList } from '../../api/banks';
import './ActiverBanqueManuelPage.css';

const ACTION_OPTIONS = [
  { value: 'unblock_sale', label: 'Débloquer Vente' },
  { value: 'block_sale', label: 'Bloquer Vente' },
  { value: 'unblock_login', label: 'Débloquer Banque (connexion)' },
  { value: 'block_login', label: 'Bloquer Banque (connexion)' },
];

const ACTION_HINTS = {
  block_sale: "Le bank reste connectable et peut toujours consulter ses rapports — seule la création de fiches sera refusée.",
  unblock_sale: "Autorise à nouveau la création de fiches pour ce scope.",
  block_login: "Le bank ne pourra plus se connecter du tout à l'app (même pour consulter ses rapports).",
  unblock_login: "Autorise à nouveau la connexion à l'app pour ce scope.",
};

const filterApiValue = (filter) => {
  if (filter === 'Groupe') return 'Group';
  if (filter === 'Banque') return 'Bank';
  return 'All';
};

const ActiverBanqueManuelPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [action, setAction] = useState('block_sale');

  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [rows, setRows] = useState(null);

  const loadList = async () => {
    if (filter !== 'Tout' && !scopeId) {
      setError(`Choisis un(e) ${filter.toLowerCase()}.`);
      return;
    }
    setLoadingList(true);
    setError('');
    try {
      const data = await fetchBankStatusList({
        filter: filterApiValue(filter),
        scope: filter === 'Tout' ? undefined : scopeId,
      });
      setRows(data);
    } catch (err) {
      setError('Chargement impossible.');
      setRows(null);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadList();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (filter !== 'Tout' && !scopeId) {
      setError(`Choisis un(e) ${filter.toLowerCase()}.`);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await bulkBankStatus({
        action,
        filter: filterApiValue(filter),
        scope: filter === 'Tout' ? undefined : scopeId,
      });
      setNotice(`${result.updated} banque(s) mise(s) à jour.`);
      await loadList();
    } catch (err) {
      setError('Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <p className="avm-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/parametres" className="bc-link">Parametres</Link>
        {' >> '}
        <span className="bc-current">Act/Desactiver Ventes (Manuel)</span>
      </p>

      <div className="avm-card">
        <div className="avm-card-header">
          <span className="avm-card-title">Act/Desactiver Ventes (Manuel)</span>
        </div>
        <div className="avm-card-body">
          <div className="avm-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="avm-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); setNotice(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="avm-row avm-scope-row">
              <label className="avm-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="avm-input avm-input-sm"
                  iconClass="avm-icon-btn"
                  nameClass="avm-copy-name"
                  errClass="avm-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="avm-input avm-input-sm"
                  iconClass="avm-icon-btn"
                  nameClass="avm-copy-name"
                  errClass="avm-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="avm-row">
            <button type="submit" className="avm-apply-btn" disabled={loadingList}>
              {loadingList ? 'Recherche...' : 'Chercher'}
            </button>
          </form>

          <form onSubmit={submit}>
            <div className="avm-row">
              <label className="avm-label">Action</label>
              <select className="avm-select" value={action} onChange={e => setAction(e.target.value)}>
                {ACTION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            <p className="avm-action-hint">{ACTION_HINTS[action]}</p>

            {error && <p className="avm-error">{error}</p>}
            {notice && <p className="avm-ok">{notice}</p>}

            <button type="submit" className="avm-apply-btn" disabled={busy}>
              {busy ? 'Application...' : 'Appliquer'}
            </button>
          </form>
        </div>
      </div>

      {rows && (
        <div className="avm-card avm-results">
          <div className="avm-card-header">
            <span className="avm-card-title">Statut des banques</span>
          </div>
          <div className="avm-card-body">
            {rows.length === 0 ? (
              <p className="avm-empty">Aucune banque sur cette portée.</p>
            ) : (
              <table className="avm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Banque</th>
                    <th>Username</th>
                    <th>Groupe</th>
                    <th>Vente</th>
                    <th>Connexion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.seq}</td>
                      <td>{r.name}</td>
                      <td>{r.username || '—'}</td>
                      <td>{r.group_name || '—'}</td>
                      <td>
                        <span className={r.sale_enabled ? 'avm-status-ok' : 'avm-status-blocked'}>
                          {r.sale_enabled ? 'Active' : 'Bloquée'}
                        </span>
                      </td>
                      <td>
                        <span className={r.login_blocked ? 'avm-status-blocked' : 'avm-status-ok'}>
                          {r.login_blocked ? 'Bloquée' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ActiverBanqueManuelPage;
